import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  signal,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EmailValidationService } from '../../../services/email-validation.service';
import { LoginFlow } from '../../../types/auth.types';
import { OtpBlockComponent } from '../otp-block/otp-block.component';
import { LoginState, ToAccessState } from '../../../types/login.types';
import { LoginService } from '../../../services/login.service';
import { LogoHeaderComponent } from '../shared/logo-header/logo-header.component';
import { LoadingSpinnerComponent } from '../shared/loading-spinner/loading-spinner.component';
import { TipoProveedorOAuthEnum } from '../../../constants/tipo-proveedor-oauth.enum';
import {
  TipoVerificacion,
  TipoVerificacionEnum,
} from '../../../constants/tipo-verificacion.enum';

export enum AccessMethod {
  CHOOSE = 'choose',
  SMS = 'sms',
  EMAIL = 'email',
  LOGIN_EMAIL = 'password',
}

export interface AccessData {
  email: string;
  phone: string;
  hasPassword: boolean;
  selectedMethod: AccessMethod;
  otpCode: string[];
  password: string;
  countdown: number;
}

@Component({
  selector: 'app-to-access',
  imports: [
    CommonModule,
    FormsModule,
    OtpBlockComponent,
    LogoHeaderComponent,
    LoadingSpinnerComponent,
  ],
  templateUrl: './to-access.component.html',
})
export class ToAccessComponent implements OnInit {
  currentState: ToAccessState | undefined | null;

  @Output() onSmsOtpRequested = new EventEmitter<LoginFlow>();
  @Output() onEmailOtpRequested = new EventEmitter<LoginFlow>();
  @Output() onBack = new EventEmitter<void>();
  @Output() onAccessGranted = new EventEmitter<AccessData>();

  readonly TipoVerificacionEnum = TipoVerificacionEnum;

  // Si es con contraseña, no ocupa verificación
  selectedMethod = signal<TipoVerificacion | null>(null);
  password = '';

  // Para el envío de OTP
  isOtpSending = signal<boolean>(false);
  otpSent = signal<boolean>(false);

  // Para el inicio de sesión (sea con contraseña o con OTP)
  isLoading = signal<boolean>(false);

  idVerificacionVinculacion = signal<number>(0);
  countdown = signal<number>(30);
  showPassword: boolean = false;
  rememberPassword: boolean = false;

  loginError = signal<string | null>(null);

  constructor(
    private emailValidationService: EmailValidationService,
    private loginService: LoginService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.loginService.loginState$.subscribe((state) => {
      if (state.type === LoginState.toAccess) {
        this.currentState = state as ToAccessState;
      }
    });
  }

  ngOnInit(): void {
    this.loadSavedPassword();
  }

  selectMethod(method: TipoVerificacion | null): void {
    this.selectedMethod.set(method);
    this.otpSent.set(false);
  }

  sendOtp(): void {
    if (this.selectedMethod() === TipoVerificacionEnum.LOGIN_CON_TELEFONO) {
      this.sendSmsOtp();
    } else if (this.selectedMethod() === TipoVerificacionEnum.LOGIN_CON_EMAIL) {
      this.sendEmailOtp();
    }
  }

  sendSmsOtp(): void {
    this.isOtpSending.set(true);
    this.emailValidationService
      .generateSmsOtpForExistingUser({
        idTelefono: this.currentState!.loginFlow.idTelefono,
      })
      .subscribe({
        next: (response) => {
          console.log('OTP response sent', response);
          if (response?.success && response?.data) {
            this.otpSent.set(true);
            this.idVerificacionVinculacion.set(response.data.idVerificacion);
          }
          this.isOtpSending.set(false);
        },
        error: (error) => {
          console.error('Error sending SMS OTP', error);
          this.isOtpSending.set(false);
        },
      });
  }

  sendEmailOtp(): void {
    this.isOtpSending.set(true);

    this.emailValidationService
      .generateEmailOtpForExistingUser({
        email: this.currentState!.loginFlow.email,
      })
      .subscribe({
        next: (response) => {
          console.log('OTP response sent', response);
          if (response?.success && response?.data) {
            this.otpSent.set(true);
            this.idVerificacionVinculacion.set(response.data.idVerificacion);
          }
          this.isOtpSending.set(false);
        },
        error: (error) => {
          console.error('Error sending Email OTP', error);
          this.isOtpSending.set(false);
        },
      });
  }

  login(): void {
    if (!this.isPasswordValid()) {
      this.loginError.set('Por favor ingresa tu contraseña');
      return;
    }

    this.isLoading.set(true);
    this.loginError.set(null);

    this.emailValidationService
      .login({
        identificador: this.currentState!.loginFlow.email,
        password: this.password,
      })
      .subscribe({
        next: (response) => {
          if (response?.success && response?.data) {
            if (this.rememberPassword) {
              this.savePassword();
            } else {
              this.clearSavedPassword();
            }

            this.loginService.notifyLoginState({
              type: LoginState.finish,
              timestamp: new Date(),
              loginFlow: {
                email: this.currentState!.loginFlow.email,
                idProveedor: TipoProveedorOAuthEnum.CREDENCIAL,
                name: response.data.user.nombrePreferido,
              },
              user: response.data.user,
              success: true,
            });
            return;
          }

          this.loginError.set(response.message);
          this.isLoading.set(false);
        },
        error: () => {
          this.loginError.set('Ocurrió un error al iniciar sesión');
          this.isLoading.set(false);
        },
      });
  }

  verifyOtp(otpCode: string): void {
    this.isLoading.set(true);

    this.emailValidationService
      .loginWithOtp({
        otp: otpCode,
        idVerificacion: this.idVerificacionVinculacion(),
        email: this.currentState!.loginFlow.email,
        tipoVerificacion: TipoVerificacionEnum.LOGIN_CON_EMAIL,
      })
      .subscribe({
        next: (response) => {
          if (response?.success && response?.data) {
            if (this.rememberPassword) {
              this.savePassword();
            } else {
              this.clearSavedPassword();
            }

            this.loginService.notifyLoginState({
              type: LoginState.finish,
              timestamp: new Date(),
              loginFlow: {
                email: this.currentState!.loginFlow.email,
                idProveedor: TipoProveedorOAuthEnum.CREDENCIAL,
                name: response.data?.user.nombrePreferido || '',
              },
              user: response.data.user,
              success: true,
            });
            return;
          }

          this.loginError.set(response.message);
          this.isLoading.set(false);
        },
        error: () => {
          this.loginError.set('Ocurrió un error al iniciar sesión');
          this.isLoading.set(false);
        },
      });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  private isPasswordValid(): boolean {
    if (!this.password) {
      this.loginError.set('Por favor ingresa tu contraseña');
      return false;
    }
    return true;
  }

  private loadSavedPassword(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const savedData = localStorage.getItem(
      `savedPassword_${this.currentState!.loginFlow.email}`
    );
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        this.password = atob(data.password);
        this.rememberPassword = true;
      } catch (error) {
        this.clearSavedPassword();
      }
    }
  }

  private savePassword(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const dataToSave = {
      password: btoa(this.password),
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(
      `savedPassword_${this.currentState!.loginFlow.email}`,
      JSON.stringify(dataToSave)
    );
  }

  private clearSavedPassword(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    localStorage.removeItem(
      `savedPassword_${this.currentState!.loginFlow.email}`
    );
  }
}
